import { createTheme, type ThemeOptions } from '@mui/material/styles';

export type ThemeMode = 'dark' | 'light';

const primaryColor = '#FF2D6D';
const secondaryColor = '#9D4EDD';
const successColor = '#34D399';
const dangerColor = '#EF4444';
const paletteByMode = (mode: ThemeMode) => ({
  mode,
  primary: {
    main: primaryColor,
    light: '#FF5C9F',
    dark: '#C70052',
  },
  secondary: {
    main: secondaryColor,
    light: '#B07CF7',
    dark: '#7C3AED',
  },
  success: {
    main: successColor,
  },
  error: {
    main: dangerColor,
  },
  warning: {
    main: '#F59E0B',
  },
  background: {
    default: mode === 'dark' ? '#12121a' : '#f5f5f5',
    paper: mode === 'dark' ? '#1c1c29' : '#ffffff',
  },
  text: {
    primary: mode === 'dark' ? '#f1f1f5' : '#111111',
    secondary: mode === 'dark' ? '#b5b5c3' : '#4b4b4b',
  },
  divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
});

const typography: ThemeOptions['typography'] = {
  fontFamily: '"Inter", "Space Grotesk", sans-serif',
  button: {
    fontWeight: 600,
    textTransform: 'none',
  },
  h1: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    fontFamily: '"Oswald", "Barlow Condensed", "Inter", sans-serif',
  },
  h2: {
    fontWeight: 700,
    letterSpacing: '-0.01em',
    fontFamily: '"Oswald", "Barlow Condensed", "Inter", sans-serif',
  },
  h3: {
    fontWeight: 600,
    fontFamily: '"Oswald", "Barlow Condensed", "Inter", sans-serif',
  },
  h4: {
    fontWeight: 600,
    fontFamily: '"Oswald", "Barlow Condensed", "Inter", sans-serif',
  },
  h5: {
    fontFamily: '"Oswald", "Barlow Condensed", "Inter", sans-serif',
  },
  h6: {
    fontFamily: '"Oswald", "Barlow Condensed", "Inter", sans-serif',
  },
};

const components = (mode: ThemeMode) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        padding: '10px 22px',
        boxShadow: 'none',
      },
      containedPrimary: {
        backgroundColor: primaryColor,
        color: '#fff',
        '&:hover': {
          backgroundColor: '#ff528c',
          filter: 'none',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'dark' ? '#1c1c29' : '#ffffff',
        border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 16,
        boxShadow: mode === 'dark'
          ? '0 25px 40px rgba(0, 0, 0, 0.75)'
          : '0 20px 45px rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 10,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: {
        transform: 'translate(14px, 16px)',
        lineHeight: 1.2,
        '&.MuiInputLabel-sizeSmall': {
          transform: 'translate(14px, 9px)',
        },
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px)',
          maxWidth: 'calc(100% - 32px)',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        '& > legend': {
          fontSize: '1em',
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: mode === 'dark' ? '#0c0c12' : '#ffffff',
        borderRight: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'dark' ? '#12121a' : '#ffffff',
        borderBottom: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        fontWeight: 600,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        height: 10,
        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
      },
      bar: {
        borderRadius: 4,
        backgroundImage: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      "@media (max-height: 800px)": {
        html: { fontSize: "15px" }, // reduz a base global em telas “baixas”
      },
    },
  },
});

export const createContactTelTheme = (mode: ThemeMode = 'dark') => createTheme({
  palette: paletteByMode(mode),
  typography,
  shape: {
    borderRadius: 12,
  },
  components: components(mode),
});
